import type { ToolApprovalCardProps, ToolApprovalRenderer } from "#components/ui/tool-approval/renderers/ToolApprovalRenderer.types.ts";
import { DraftSurface, EditErrors, FieldGroup, SentText, textEditor, useDraftMode } from "#features/conversations/components/prepared-action/cardControls.tsx";
import { DraftTextArea, type DraftEditor } from "#features/conversations/components/prepared-action/DraftTextArea.tsx";
import { isObject, readString, readStringList, type Fields } from "#features/conversations/lib/untypedFields.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import type { ToolApprovalEditError } from "#types/ToolApproval.ts";
import { Button, H3, Select, SelectItem, Text, TextField } from "@hopper-ui/components";
import { IconDocumentAdd, IconDocumentEdit, IconDocumentX, IconLock, IconPeople } from "@intentui/icons";
import { useState, type ComponentType, type Key } from "react";
import "./SkillResourceCard.css";

type Mode = "create" | "update" | "delete";

// Core's bounds on a resource (skill-resources spec R3).
const NAME_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 500;

// A template this long collapses behind "Show all", so the decision stays in view.
const COLLAPSE_ABOVE_LINES = 6;
const COLLAPSE_ABOVE_CHARACTERS = 480;

// The categories and roles this client can name; any other value core sends renders as received.
const KNOWN_CATEGORIES = new Set(["recurring", "growth_and_planning", "high_stakes", "life_cycle"]);
const KNOWN_AUDIENCE_ROLES = new Set(["managers", "admins"]);

// Stands in for "no category" in the picker; the edit sends null.
const NO_CATEGORY_KEY = "__none__";

const MODE_GLYPH: Record<Mode, ComponentType<{ className?: string; "aria-hidden"?: boolean | "true" }>> = {
    create: IconDocumentAdd,
    update: IconDocumentEdit,
    delete: IconDocumentX
};

const MODE_LABEL: Record<Mode, string> = {
    create: "skillTemplate.kindCreate",
    update: "skillTemplate.kindUpdate",
    delete: "skillTemplate.kindDelete"
};

function useCategoryLabel() {
    const { tShared } = useSharedTranslation();

    return (category: string) => (KNOWN_CATEGORIES.has(category) ? tShared(`skillTemplate.category.${category}`) : category);
}

// The edit wins over the arg, then the target's current value, so the field shows what will be saved.
function currentCategory(args: Fields, edits: Fields, fields: Fields): string | null {
    if ("category" in edits) {
        return typeof edits.category === "string" ? edits.category : null;
    }

    return readString(args, "category") ?? readString(fields, "current_category");
}

// Roles first, then segments by name, or by id when core could not name one. Null for a field core did not send.
function useAudienceLabel() {
    const { tShared } = useSharedTranslation();

    return (fields: Fields, key: string): string | null => {
        const audience = fields[key];
        if (audience === null) {
            return tShared("skillTemplate.audienceEveryone");
        }
        if (!isObject(audience)) {
            return null;
        }
        const segments = (Array.isArray(audience.segments) ? audience.segments : []).filter(isObject).flatMap(segment => {
            const id = readString(segment, "id");

            return id === null ? [] : [readString(segment, "name") ?? id];
        });
        const roles = readStringList(audience, "roles").map(role => (KNOWN_AUDIENCE_ROLES.has(role) ? tShared(`skillTemplate.audienceRole.${role}`) : role));

        return [...roles, ...segments].join(", ");
    };
}

// Read-only: to change who it targets, the manager asks the assistant.
function AudienceField({ label, current }: { label: string; current?: string | null }) {
    const { tShared } = useSharedTranslation();

    return (
        <FieldGroup label={tShared("skillTemplate.audienceLabel")}>
            {() => (
                <>
                    <Text size="sm">{label}</Text>
                    {current != null && current !== label && (
                        <Text size="xs" className="wai-pa-muted">{tShared("skillTemplate.currently", { name: current })}</Text>
                    )}
                </>
            )}
        </FieldGroup>
    );
}

function errorsFor(errors: ToolApprovalEditError[], field: string): ToolApprovalEditError[] {
    return errors.filter(error => error.field === field);
}

// An update that omits a field keeps the target's current value, so the field starts from it.
function fieldEditor(props: ToolApprovalCardProps, key: string, current: string | null): DraftEditor {
    const { args, edits, onEditArgs } = props;
    if (readString(args, key) === null && typeof edits[key] !== "string" && current !== null) {
        return { value: current, onChange: (value: string) => onEditArgs({ [key]: value }) };
    }

    return textEditor(props, key);
}

// Where the template lives, at the header's end: who will see it.
function ScopeLabel({ scope }: { scope: string }) {
    const { tShared } = useSharedTranslation();
    const isOrganization = scope === "organization";

    return (
        <Text size="xs" fontWeight="body-xs-medium" className="wai-srt-scope">
            {isOrganization ? <IconPeople aria-hidden="true" /> : <IconLock aria-hidden="true" />}
            {tShared(isOrganization ? "skillTemplate.scopeOrganization" : "skillTemplate.scopeUser")}
        </Text>
    );
}

function CategoryPicker({ allowed, selected, allowNone, errors, isDisabled, onChange }: {
    allowed: string[];
    selected: string | null;
    allowNone: boolean;
    errors: ToolApprovalEditError[];
    isDisabled: boolean;
    onChange: (category: string | null) => void;
}) {
    const { tShared } = useSharedTranslation();
    const labelOf = useCategoryLabel();

    return (
        <FieldGroup label={tShared("skillTemplate.categoryLabel")}>
            {labelId => (
                <>
                    <Select
                        aria-labelledby={labelId}
                        placeholder={tShared("skillTemplate.chooseCategory")}
                        selectedKey={selected ?? (allowNone ? NO_CATEGORY_KEY : null)}
                        onSelectionChange={(key: Key | null) => onChange(key === null || key === NO_CATEGORY_KEY ? null : String(key))}
                        isInvalid={errors.length > 0}
                        isDisabled={isDisabled}
                        isFluid
                        className="wai-pa-field wai-pa-input wai-srt-select"
                    >
                        {[
                            ...(allowNone ? [<SelectItem key={NO_CATEGORY_KEY} id={NO_CATEGORY_KEY}>{tShared("skillTemplate.noCategory")}</SelectItem>] : []),
                            ...allowed.map(category => <SelectItem key={category} id={category}>{labelOf(category)}</SelectItem>)
                        ]}
                    </Select>
                    <EditErrors errors={errors} />
                </>
            )}
        </FieldGroup>
    );
}

// The template body as the manager will get it: plain text on the card's faint fill (renderer spec R6).
function TemplatePreview({ content, label }: { content: string; label: string }) {
    const { tShared } = useSharedTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const isLong = content.split("\n").length > COLLAPSE_ABOVE_LINES || content.length > COLLAPSE_ABOVE_CHARACTERS;
    const isCollapsed = isLong && !isExpanded;

    return (
        <FieldGroup label={label}>
            {labelId => (
                <>
                    <div aria-labelledby={labelId} role="region" className={isCollapsed ? "wai-srt-preview wai-srt-preview-collapsed" : "wai-srt-preview"}>
                        <SentText>{content}</SentText>
                    </div>
                    {isLong && (
                        <Button variant="ghost-secondary" size="xs" className="wai-pa-btn wai-pa-btn-plain wai-srt-toggle" onPress={() => setIsExpanded(value => !value)}>
                            <Text>{tShared(isExpanded ? "skillTemplate.showLess" : "skillTemplate.showAll")}</Text>
                        </Button>
                    )}
                </>
            )}
        </FieldGroup>
    );
}

function SkillResourceCard({ mode, ...props }: ToolApprovalCardProps & { mode: Mode }) {
    const { args, display, edits, onEditArgs, editErrors } = props;
    const { tShared } = useSharedTranslation();
    const labelOf = useCategoryLabel();
    const audienceLabelOf = useAudienceLabel();
    const fields = display ?? {};
    // A skill slug means nothing to a manager; without core's name the header shows the kind alone.
    const skillName = readString(fields, "skill_name");
    // Only a save carries its scope as an argument; an update or delete of an unknown scope shows none.
    const scope = readString(fields, "scope") ?? (mode === "create" ? (readString(args, "scope") ?? "user") : null);
    const currentName = readString(fields, "current_name");
    const currentDescription = readString(fields, "current_description");
    const allowed = readStringList(fields, "allowed_categories");
    const category = currentCategory(args, edits, fields);
    const content = readString(args, "content");
    // Core sends a null audience for a My templates template too, which has none to show.
    const audience = scope === "organization" ? audienceLabelOf(fields, mode === "delete" ? "current_audience" : "audience") : null;
    const showsCategory = mode !== "delete" && allowed.length > 0;
    // A field's error shows under it; an error for a field that isn't on screen shows at the card's end.
    const fieldsOnCard = mode === "delete" ? [] : showsCategory ? ["name", "description", "category"] : ["name", "description"];
    const Glyph = MODE_GLYPH[mode];
    const kind = tShared(MODE_LABEL[mode]);
    // Once the approval is answered or on its way out, the fields show what went out and take no edits.
    const isLocked = useDraftMode() !== "editing";

    return (
        <DraftSurface>
            <div className="wai-pa-header">
                <Glyph className="wai-pa-glyph" aria-hidden="true" />
                <Text size="xs" fontWeight="body-xs-medium" color="neutral-weak" className="wai-pa-label">
                    {skillName === null ? kind : `${kind} · ${skillName}`}
                </Text>
                {scope !== null && <ScopeLabel scope={scope} />}
            </div>

            {mode === "delete" ? (
                // A confirmation, not a form: what goes, in the card's title and body text.
                <div className="wai-srt-target">
                    <H3 fontSize="body-sm" fontWeight="body-sm-semibold" lineHeight="body-sm" color="neutral" className="wai-pa-title">
                        {currentName ?? readString(args, "name") ?? tShared("skillTemplate.deleteWithoutName")}
                    </H3>
                    {currentDescription !== null && <Text size="sm" color="neutral-weak">{currentDescription}</Text>}
                    {category !== null && <Text size="xs" className="wai-pa-muted">{labelOf(category)}</Text>}
                    {audience !== null && <AudienceField label={audience} />}
                </div>
            ) : (
                <>
                    <FieldGroup label={tShared("skillTemplate.name")}>
                        {labelId => {
                            const editor = fieldEditor(props, "name", mode === "update" ? currentName : null);

                            return (
                                <>
                                    <TextField
                                        aria-labelledby={labelId}
                                        value={editor.value}
                                        onChange={editor.onChange}
                                        maxLength={NAME_MAX_LENGTH}
                                        isInvalid={errorsFor(editErrors, "name").length > 0}
                                        isDisabled={isLocked}
                                        isFluid
                                        className="wai-pa-field wai-pa-input"
                                    />
                                    <EditErrors errors={errorsFor(editErrors, "name")} />
                                    {mode === "update" && currentName !== null && editor.value !== currentName && (
                                        <Text size="xs" className="wai-pa-muted">{tShared("skillTemplate.currently", { name: currentName })}</Text>
                                    )}
                                </>
                            );
                        }}
                    </FieldGroup>
                    <div className="wai-pa-field-group">
                        <DraftTextArea
                            editor={fieldEditor(props, "description", mode === "update" ? currentDescription : null)}
                            label={tShared("skillTemplate.description")}
                            maxLength={DESCRIPTION_MAX_LENGTH}
                            isInvalid={errorsFor(editErrors, "description").length > 0}
                            isDisabled={isLocked}
                            isLabelVisible
                        />
                        <EditErrors errors={errorsFor(editErrors, "description")} />
                    </div>
                    {showsCategory && (
                        <CategoryPicker
                            allowed={allowed}
                            selected={category}
                            allowNone={mode === "create"}
                            errors={errorsFor(editErrors, "category")}
                            isDisabled={isLocked}
                            onChange={value => onEditArgs({ category: value })}
                        />
                    )}
                    {audience !== null && <AudienceField label={audience} current={mode === "update" ? audienceLabelOf(fields, "current_audience") : null} />}
                    {content !== null && (
                        <TemplatePreview content={content} label={tShared(mode === "update" ? "skillTemplate.newContent" : "skillTemplate.content")} />
                    )}
                </>
            )}
            <EditErrors errors={editErrors.filter(error => error.field === null || !fieldsOnCard.includes(error.field))} />
        </DraftSurface>
    );
}

export const createSkillResourceRenderer: ToolApprovalRenderer = {
    matches: args => readString(args, "name") !== null && readString(args, "content") !== null,
    Card: props => <SkillResourceCard mode="create" {...props} />,
    decisionLabels: { allow: "skillTemplate.save", deny: "skillTemplate.dontSave" }
};

export const updateSkillResourceRenderer: ToolApprovalRenderer = {
    matches: args => readString(args, "resource_id") !== null,
    Card: props => <SkillResourceCard mode="update" {...props} />,
    decisionLabels: { allow: "skillTemplate.update", deny: "skillTemplate.keepAsIs" }
};

export const deleteSkillResourceRenderer: ToolApprovalRenderer = {
    matches: args => readString(args, "resource_id") !== null,
    Card: props => <SkillResourceCard mode="delete" {...props} />,
    decisionLabels: { allow: "skillTemplate.delete", deny: "skillTemplate.keepIt" }
};
